import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useMemo, createElement, useRef, useEffect, useCallback, useState } from "react";
import ReactSelect, { components, defaultTheme, createFilter } from "react-select";
import styled, { css } from "styled-components";
import { IconButton } from "@creditornot/cb-button";
import { typographyCss, cbSemanticCssVars, colors, elevationCss } from "@creditornot/cb-ingredients";
import { MenuItemGroup, MenuActionItem } from "@creditornot/cb-menu";
import { Spinner } from "@creditornot/cb-progress";
import { Tag } from "@creditornot/cb-tag";
import { createCssVars, createThemedCssVarDefinitions } from "@creditornot/cb-utils";
import debounce from "lodash.debounce";
import ReactSelectAsync from "react-select/async";
import ReactSelectCreatable from "react-select/creatable";
const SvgCaretdown = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12.028 19.5c.662 0 1.292-.283 1.73-.778l9.81-11.13a1.84 1.84 0 00-.195-2.568 1.85 1.85 0 00-2.578.134l-8.579 9.732a.25.25 0 01-.376 0L3.262 5.158a1.849 1.849 0 00-3.17.609A1.84 1.84 0 00.49 7.592l9.807 11.126a2.312 2.312 0 001.732.782z" }) }));
const CaretDownIcon = SvgCaretdown;
const SvgCross = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M2.44 19.44a1.5 1.5 0 002.121 2.121l7.262-7.26a.25.25 0 01.354 0l7.262 7.262a1.5 1.5 0 102.122-2.12l-7.26-7.266a.25.25 0 010-.354l7.262-7.262a1.5 1.5 0 00-2.124-2.12L12.177 9.7a.25.25 0 01-.354 0L4.561 2.44A1.5 1.5 0 002.44 4.56L9.7 11.823a.25.25 0 010 .354L2.44 19.44z" }) }));
const CrossIcon = SvgCross;
const BORDER_WIDTH = 1;
const HEIGHT = 36;
const HEIGHT_WITHOUT_BORDER = HEIGHT - BORDER_WIDTH * 2;
const colorCssVars = createCssVars(["background", "backgroundDisabled", "borderHover"]);
const fontStyle = css`
  ${typographyCss.Body3()}
  color: ${cbSemanticCssVars.textPrimary};
`;
const ControlRoot = styled.div`
  ${createThemedCssVarDefinitions({
  background: {
    light: colors.salt100,
    dark: "transparent"
  },
  backgroundDisabled: {
    light: colors.pepper8,
    dark: colors.salt12
  },
  borderHover: {
    light: colors.pepper20,
    lightHighContrast: colors.pepper40,
    dark: colors.salt20
  }
})}

  position: relative;
  z-index: 0;

  display: flex;
  justify-content: space-between;
  align-items: stretch;
  outline: none;

  ${fontStyle}
  color: ${cbSemanticCssVars.textPrimary};
  min-height: ${HEIGHT}px;
  min-width: 96px;
  flex-wrap: nowrap;
  box-sizing: border-box;
  box-shadow: none;
  border-radius: 8px;
  border-color: ${({ $invalid, $isFocused, $open }) => {
  if ($invalid && !$isFocused) {
    return colors.strawberry100;
  }
  if ($open || $isFocused) {
    return colors.wolt100;
  }
  return cbSemanticCssVars.borderLight;
}};
  border-width: ${BORDER_WIDTH}px;
  border-style: solid;
  background-color: ${({ $disabled }) => $disabled ? colorCssVars.backgroundDisabled : colorCssVars.background};
  transition:
    background-color 75ms ease-in,
    border-color 75ms ease-in;

  ${({ $disabled, $invalid }) => {
  if ($disabled || $invalid) {
    return;
  }
  return css`
      &:hover {
        border-color: ${colorCssVars.borderHover};
      }
    `;
}}
`;
const Control = ({
  children,
  innerProps,
  innerRef,
  isDisabled,
  isFocused,
  selectProps
}) => /* @__PURE__ */ jsx(
  ControlRoot,
  {
    ...innerProps,
    $disabled: isDisabled,
    $invalid: selectProps["aria-invalid"],
    $isFocused: isFocused,
    $open: !!selectProps.menuIsOpen,
    ref: innerRef,
    children
  }
);
const IndicatorsContainerRoot = styled.div`
  display: flex;
  align-self: stretch;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  box-sizing: border-box;
  min-width: 36px;
  height: ${HEIGHT_WITHOUT_BORDER}px;
  padding-inline-end: 12px;

  > * + * {
    margin-inline-start: 4px;
  }
`;
const IndicatorsContainer = ({
  children
}) => /* @__PURE__ */ jsx(IndicatorsContainerRoot, { children });
const StyledCaretDownIcon = styled(CaretDownIcon)`
  width: 12px;
  height: 12px;
  fill: ${cbSemanticCssVars.textSecondary};
`;
const DropdownIndicator = ({}) => /* @__PURE__ */ jsx(StyledCaretDownIcon, {});
const ClearIndicator = ({
  innerProps
}) => /* @__PURE__ */ jsx(
  IconButton,
  {
    background: "animated",
    ...innerProps,
    size: "tiny",
    variant: "grey",
    children: /* @__PURE__ */ jsx(CrossIcon, {})
  }
);
const ValueContainerRoot = styled.div`
  flex: 1;
  align-self: stretch;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  color: ${cbSemanticCssVars.textPrimary};
  padding: ${({ $multiselect }) => $multiselect ? `0px 6px` : `3px 12px`};
  max-width: calc(100% - 52px);
`;
const MultiSelectItemsWrapper = styled.div`
  box-sizing: border-box;
  padding-top: 3px;
  padding-bottom: 3px;
  min-height: ${HEIGHT_WITHOUT_BORDER}px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`;
const ValueContainer = ({
  children,
  isMulti
}) => /* @__PURE__ */ jsx(ValueContainerRoot, { $multiselect: isMulti, children: isMulti ? /* @__PURE__ */ jsx(MultiSelectItemsWrapper, { children }) : children });
const GroupRoot = styled.div`
  padding-top: 8px;

  button + & {
    margin-top: 16px;
  }

  & + & {
    margin-top: 16px;
  }
`;
const Group = ({
  children,
  label
}) => /* @__PURE__ */ jsx(GroupRoot, { children: /* @__PURE__ */ jsx(MenuItemGroup, { title: label, children }) });
const Option = ({
  children,
  className,
  innerProps,
  innerRef,
  isDisabled,
  isFocused,
  isSelected
}) => /* @__PURE__ */ jsx(
  MenuActionItem,
  {
    className,
    disabled: isDisabled,
    focused: isFocused,
    id: innerProps.id,
    onClick: innerProps.onClick,
    onMouseMove: innerProps.onMouseMove,
    onMouseOver: innerProps.onMouseOver,
    ref: innerRef,
    selected: isSelected,
    tabIndex: innerProps.tabIndex,
    variant: "black",
    children
  },
  innerProps.key
);
const MenuRoot = styled.div`
  position: absolute;
  z-index: 1;
  bottom: ${({ $bottom }) => $bottom || "auto"};
  top: ${({ $top }) => $top || "auto"};
  left: 0;
  right: 0;
  max-width: 100%;
  margin-bottom: 4px;
  margin-top: 4px;
  border-radius: 8px;
  box-sizing: border-box;
  ${fontStyle}
  ${elevationCss(8)}
  box-shadow: 0 4px 14px -2px ${colors.pepper12};
  border: 1px solid ${cbSemanticCssVars.borderLight};
  min-height: ${({ $minMenuHeight }) => $minMenuHeight}px;
  max-height: ${({ $maxMenuHeight }) => $maxMenuHeight}px;
  overflow-y: hidden;
`;
const MenuScroller = styled.div`
  height: 100%;
  min-height: ${({ $minMenuHeight }) => $minMenuHeight}px;
  max-height: ${({ $maxMenuHeight }) => $maxMenuHeight}px;
  overflow-y: auto;
`;
const Menu = (props) => {
  const styles = props.getStyles("menu", props);
  return /* @__PURE__ */ jsx(
    MenuRoot,
    {
      ...props.innerProps,
      $bottom: styles.bottom,
      $maxMenuHeight: props.maxMenuHeight,
      $minMenuHeight: props.minMenuHeight,
      $top: styles.top,
      ref: props.innerRef,
      children: /* @__PURE__ */ jsx(MenuScroller, { $maxMenuHeight: props.maxMenuHeight, $minMenuHeight: props.minMenuHeight, children: props.children })
    }
  );
};
const MenuListRoot = styled.div`
  padding: 8px 16px;
`;
const MenuList = ({
  children
}) => /* @__PURE__ */ jsx(MenuListRoot, { children: /* @__PURE__ */ jsx(MenuItemGroup, { children }) });
const StyledTag = styled(Tag)`
  max-width: 100%;
  margin-bottom: 2px;
  margin-top: 2px;
  margin-inline-end: 4px;
  border-radius: 4px;
`;
const MultiValue = ({
  children,
  isDisabled,
  removeProps
}) => /* @__PURE__ */ jsx(
  StyledTag,
  {
    removeButtonProps: removeProps,
    removeDisabled: isDisabled,
    variant: "lightBlue",
    children
  }
);
const PlaceholderRoot = styled.div`
  position: absolute;
  top: 0;
  inset-inline-start: 12px;
  bottom: 0;
  color: ${cbSemanticCssVars.textSecondary};

  display: flex;
  align-items: center;
`;
const Placeholder = ({
  children
}) => /* @__PURE__ */ jsx(PlaceholderRoot, { children });
const LoadingIndicatorRoot = styled.div`
  padding-inline-start: 4px;
  padding-inline-end: 4px;
  display: flex;
  align-items: center;
`;
const LoadingIndicator = () => /* @__PURE__ */ jsx(LoadingIndicatorRoot, { children: /* @__PURE__ */ jsx(Spinner, { color: "lightGrey", size: "small" }) });
const LoadingMessageRoot = styled.div`
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${cbSemanticCssVars.textSecondary};
`;
const LoadingMessage = () => /* @__PURE__ */ jsxs(LoadingMessageRoot, { children: [
  /* @__PURE__ */ jsx(Spinner, { color: "lightGrey", size: "small", style: { marginInlineEnd: 8 } }),
  "Loading..."
] });
const SingleValueRoot = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 44px);
`;
const SingleValue = ({
  children
}) => /* @__PURE__ */ jsx(SingleValueRoot, { children });
const MultiSelectInputContainer = styled.div`
  margin-inline-start: 6px;
`;
const Input = (props) => {
  if (props.isMulti) {
    return /* @__PURE__ */ jsx(MultiSelectInputContainer, { children: /* @__PURE__ */ jsx(components.Input, { ...props }) });
  }
  return /* @__PURE__ */ jsx(components.Input, { ...props });
};
const createOptionComponent = (OptionComponent) => (
  /* eslint-disable-next-line react/display-name */
  ({
    className,
    data,
    innerProps,
    innerRef,
    isDisabled,
    isFocused,
    isSelected
  }) => createElement(OptionComponent, {
    option: data,
    className,
    disabled: isDisabled,
    focused: isFocused,
    selected: isSelected,
    id: innerProps.id,
    key: innerProps.key,
    onClick: innerProps.onClick,
    onMouseMove: innerProps.onMouseMove,
    onMouseOver: innerProps.onMouseOver,
    innerRef,
    tabIndex: innerProps.tabIndex
  })
);
function useCustomComponents({
  optionComponent
}) {
  return useMemo(
    () => ({
      Control,
      IndicatorSeparator: null,
      IndicatorsContainer,
      LoadingIndicator,
      LoadingMessage,
      DropdownIndicator,
      Group,
      ValueContainer,
      Option: optionComponent ? createOptionComponent(optionComponent) : Option,
      Menu,
      MenuList,
      MultiValue,
      ClearIndicator,
      Placeholder,
      SingleValue,
      Input
    }),
    [optionComponent]
  );
}
function useDebounce(func, wait, options, deps = []) {
  const debouncedFnRef = useRef();
  debouncedFnRef.current = useMemo(() => {
    if (debouncedFnRef.current) {
      debouncedFnRef.current.cancel();
    }
    return debounce(func, wait, options);
  }, deps);
  useEffect(() => () => debouncedFnRef.current && debouncedFnRef.current.cancel(), []);
  return debouncedFnRef.current;
}
const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
};
const isOptionGroup = (value) => typeof value === "object" && value.hasOwnProperty("options") && Array.isArray(value.options);
const isDefined = (val) => val !== void 0;
const useSelectionOrder = ({
  options,
  selectionOrder,
  value
}) => {
  return useMemo(() => {
    const flatOptions = options.flatMap((opt) => isOptionGroup(opt) ? opt.options : opt);
    if (selectionOrder == "asEntered") {
      return value.map((v) => flatOptions.find((opt) => opt.value === v)).filter(isDefined);
    }
    return flatOptions.filter((opt) => value.includes(opt.value));
  }, [options, value, selectionOrder]);
};
const theme = {
  colors: {
    ...defaultTheme.colors,
    primary: cbSemanticCssVars.textPrimary
  },
  borderRadius: 8,
  spacing: {
    /* Used to calculate consistent margin/padding on elements */
    baseUnit: 4,
    /* The minimum height of the control */
    controlHeight: 36,
    /* The amount of space between the control and menu */
    menuGutter: 4
  }
};
const baseStyles = {
  input: (base, { theme: theme2 }) => ({
    ...base,
    marginLeft: 0,
    color: theme2.colors.primary
  }),
  singleValue: (base, { theme: theme2 }) => ({
    ...base,
    marginLeft: 0,
    color: theme2.colors.primary
  })
};
const SelectBase = ({
  autoFocus,
  blurInputOnSelect,
  className,
  clearable,
  closeMenuOnScroll,
  closeMenuOnSelect,
  disabled,
  disableSearch,
  getOptionValueForSearch,
  inputId,
  invalid,
  maxMenuHeight = 300,
  menuPlacement = "auto",
  menuPortalTarget,
  minMenuHeight = 48,
  multiselect,
  onBlur,
  onChange,
  onFocus,
  optionComponent,
  options = [],
  placeholder,
  selectRef,
  style,
  value
}) => {
  const components2 = useCustomComponents({ optionComponent });
  const filterOption = useMemo(
    () => getOptionValueForSearch ? createFilter({
      stringify: (option) => getOptionValueForSearch(option.data)
    }) : void 0,
    [getOptionValueForSearch]
  );
  return /* @__PURE__ */ jsx(
    ReactSelect,
    {
      "aria-invalid": invalid,
      autoFocus,
      blurInputOnSelect,
      captureMenuScroll: false,
      className: `cb-select-Select__root ${className || ""}`,
      closeMenuOnScroll,
      closeMenuOnSelect,
      components: components2,
      filterOption,
      inputId,
      isClearable: clearable,
      isDisabled: disabled,
      isMulti: multiselect,
      isSearchable: !disableSearch,
      maxMenuHeight,
      menuPlacement,
      menuPortalTarget,
      minMenuHeight,
      onBlur,
      onChange,
      onFocus,
      options,
      placeholder,
      ref: selectRef,
      styles: {
        ...baseStyles,
        container: style && ((styles) => ({ ...styles, ...style }))
      },
      theme,
      value
    }
  );
};
const Select = ({
  closeMenuOnSelect = true,
  onChange,
  options = [],
  value,
  ...rest
}) => {
  const handleChange = useCallback(
    (option) => {
      if (onChange) {
        onChange(option ? option.value : null);
      }
    },
    [onChange]
  );
  const flatOptions = useMemo(
    () => options.flatMap((opt) => isOptionGroup(opt) ? opt.options : opt),
    [options]
  );
  const selectedOption = useMemo(
    () => flatOptions.find((opt) => opt.value === value) || null,
    [flatOptions, value]
  );
  return /* @__PURE__ */ jsx(
    SelectBase,
    {
      ...rest,
      closeMenuOnSelect,
      multiselect: false,
      onChange: handleChange,
      options,
      value: selectedOption
    }
  );
};
const MultiSelect = ({
  clearable = true,
  closeMenuOnSelect = false,
  onChange,
  options = [],
  selectionOrder = "asSorted",
  value = [],
  ...rest
}) => {
  const handleChange = useCallback(
    (options2) => {
      if (onChange) {
        onChange(options2.map((option) => option.value));
      }
    },
    [onChange]
  );
  const selectedOptions = useSelectionOrder({ options, value, selectionOrder });
  return /* @__PURE__ */ jsx(
    SelectBase,
    {
      ...rest,
      clearable,
      closeMenuOnSelect,
      multiselect: true,
      onChange: handleChange,
      options,
      value: selectedOptions
    }
  );
};
const SelectBaseAsync = ({
  autoFocus,
  blurInputOnSelect,
  cacheOptions = false,
  className,
  clearable,
  closeMenuOnScroll,
  closeMenuOnSelect,
  defaultOptions = [],
  disabled,
  disableSearch,
  inputId,
  invalid,
  loadOptions = () => Promise.resolve([]),
  loadOptionsDebounce = 200,
  maxMenuHeight = 300,
  menuPlacement = "auto",
  menuPortalTarget,
  minMenuHeight = 48,
  multiselect,
  onBlur,
  onChange,
  onFocus,
  optionComponent,
  placeholder,
  selectRef,
  style,
  value
}) => {
  const components2 = useCustomComponents({ optionComponent });
  const handleLoadOptions = useCallback(
    (inputValue, callback) => {
      loadOptions(inputValue).then((res) => {
        if (res) {
          callback(res);
        } else {
          callback([]);
        }
      });
    },
    [loadOptions]
  );
  const debouncedHandleLoadOptions = useDebounce(
    handleLoadOptions,
    loadOptionsDebounce,
    { leading: false, trailing: true },
    [handleLoadOptions]
  );
  return /* @__PURE__ */ jsx(
    ReactSelectAsync,
    {
      "aria-invalid": invalid,
      autoFocus,
      blurInputOnSelect,
      cacheOptions,
      captureMenuScroll: false,
      className: `cb-select-Select__root ${className || ""}`,
      closeMenuOnScroll,
      closeMenuOnSelect,
      components: components2,
      defaultOptions,
      inputId,
      isClearable: clearable,
      isDisabled: disabled,
      isMulti: multiselect,
      isSearchable: !disableSearch,
      loadOptions: debouncedHandleLoadOptions,
      maxMenuHeight,
      menuPlacement,
      menuPortalTarget,
      minMenuHeight,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      ref: selectRef,
      styles: {
        ...baseStyles,
        container: style && ((styles) => ({ ...styles, ...style }))
      },
      theme,
      value
    }
  );
};
const SelectAsync = ({
  closeMenuOnSelect = true,
  defaultOptions = [],
  initialOption,
  loadOptions = () => Promise.resolve([]),
  onChange,
  placeholder = "Search...",
  value,
  ...rest
}) => {
  const [selectedOption, setSelectedOption] = useState(initialOption);
  const handleChange = useCallback(
    (option) => {
      if (onChange) {
        setSelectedOption(option);
        onChange(option ? option.value : null);
      }
    },
    [onChange]
  );
  return /* @__PURE__ */ jsx(
    SelectBaseAsync,
    {
      ...rest,
      closeMenuOnSelect,
      defaultOptions,
      loadOptions,
      multiselect: false,
      onChange: handleChange,
      placeholder,
      value: value === (selectedOption == null ? void 0 : selectedOption.value) ? selectedOption : void 0
    }
  );
};
const SelectBaseCreatable = ({
  className,
  clearable,
  disabled,
  disableSearch,
  invalid,
  loading,
  maxMenuHeight = 300,
  menuPlacement = "auto",
  minMenuHeight = 48,
  multiselect,
  optionComponent,
  selectRef,
  style,
  ...rest
}) => {
  const components2 = useCustomComponents({ optionComponent });
  return /* @__PURE__ */ jsx(
    ReactSelectCreatable,
    {
      ...rest,
      "aria-invalid": invalid,
      captureMenuScroll: false,
      className: `cb-select-Select__root ${className || ""}`,
      components: components2,
      isClearable: clearable,
      isDisabled: disabled,
      isLoading: loading,
      isMulti: multiselect,
      isSearchable: !disableSearch,
      maxMenuHeight,
      menuPlacement,
      minMenuHeight,
      ref: selectRef,
      styles: {
        ...baseStyles,
        container: style && ((styles) => ({
          ...styles,
          ...style
        }))
      },
      theme
    }
  );
};
const SelectCreatable = ({
  closeMenuOnSelect = true,
  onChange,
  options = [],
  placeholder = "Search...",
  value,
  ...rest
}) => {
  const [selectedOption, setSelectedOption] = useState(
    options.flatMap((opt) => isOptionGroup(opt) ? opt.options : opt).find((opt) => opt.value === value)
  );
  const handleChange = useCallback(
    (option) => {
      if (onChange) {
        setSelectedOption(option);
        onChange(option ? option.value : null);
      }
    },
    [onChange]
  );
  return /* @__PURE__ */ jsx(
    SelectBaseCreatable,
    {
      ...rest,
      closeMenuOnSelect,
      multiselect: false,
      onChange: handleChange,
      options,
      placeholder,
      value: value === (selectedOption == null ? void 0 : selectedOption.value) ? selectedOption : void 0
    }
  );
};
const MultiSelectAsync = ({
  clearable = true,
  closeMenuOnSelect = false,
  defaultOptions = [],
  initialOptions = [],
  initialOptionsEqual,
  loadOptions = () => Promise.resolve([]),
  onChange,
  placeholder = "Search...",
  value = [],
  ...rest
}) => {
  const [selectedOptions, setSelectedOptions] = useState(initialOptions);
  const previousInitialOptions = usePrevious(initialOptions);
  const handleChange = useCallback(
    (options) => {
      if (onChange) {
        setSelectedOptions(options);
        onChange(options.map((option) => option.value));
      }
    },
    [onChange]
  );
  useEffect(() => {
    if (initialOptionsEqual && initialOptions && previousInitialOptions && initialOptionsEqual(initialOptions, previousInitialOptions) === false) {
      setSelectedOptions(initialOptions);
    }
  }, [previousInitialOptions, initialOptions, initialOptionsEqual]);
  return /* @__PURE__ */ jsx(
    SelectBaseAsync,
    {
      ...rest,
      clearable,
      closeMenuOnSelect,
      defaultOptions,
      loadOptions,
      multiselect: true,
      onChange: handleChange,
      placeholder,
      value: selectedOptions.filter((opt) => value.includes(opt.value))
    }
  );
};
const MultiSelectCreatable = ({
  clearable = true,
  closeMenuOnSelect = false,
  onChange,
  options = [],
  placeholder = "Search...",
  selectionOrder = "asSorted",
  value = [],
  ...rest
}) => {
  const [createdOptions, setCreatedOptions] = useState([]);
  const handleChange = useCallback(
    (selectedOptions2) => {
      const existingOptionValues = options.flatMap(
        (opt) => isOptionGroup(opt) ? opt.options.map((grpOption) => grpOption.value) : opt.value
      );
      setCreatedOptions(selectedOptions2.filter((opt) => !existingOptionValues.includes(opt.value)));
      onChange == null ? void 0 : onChange(selectedOptions2.map((option) => option.value));
    },
    [onChange, options]
  );
  useEffect(() => {
    const existingOptionValues = options.flatMap(
      (opt) => isOptionGroup(opt) ? opt.options.map((grpOption) => grpOption.value) : opt.value
    );
    setCreatedOptions((opts) => opts.filter((opt) => !existingOptionValues.includes(opt.value)));
  }, [options]);
  const selectedOptions = useSelectionOrder({
    options: [...options, ...createdOptions],
    value,
    selectionOrder
  });
  return /* @__PURE__ */ jsx(
    SelectBaseCreatable,
    {
      ...rest,
      clearable,
      closeMenuOnSelect,
      multiselect: true,
      onChange: handleChange,
      options,
      placeholder,
      value: selectedOptions
    }
  );
};
export {
  MultiSelect,
  MultiSelectAsync,
  MultiSelectCreatable,
  Select,
  SelectAsync,
  SelectCreatable
};
//# sourceMappingURL=index.js.map
